import React from 'react'
import {
  faBook,
  faBuilding,
  faCheckToSlot,
  faCircleInfo,
  faCircleQuestion,
  faClipboardList,
  faDatabase,
  faEnvelope,
  faFileLines,
  faFingerprint,
  faGauge,
  faHeartPulse,
  faIdBadge,
  faKey,
  faLaptop,
  faLink,
  faListCheck,
  faLock,
  faMagnifyingGlass,
  faScroll,
  faShield,
  faShieldHalved,
  faSitemap,
  faSliders,
  faSquareCheck,
  faTicket,
  faTriangleExclamation,
  faUserShield,
  faUsers,
  faXmark,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import type { IconAdapter } from '@devnw/icons'

/**
 * Every icon used by this application, imported as build-time SVG data.
 *
 * Web Awesome's default icon library resolves even its free icons from
 * ka-f.fontawesome.com at runtime. That contradicts the admin SPA's strict
 * self-only CSP and creates an unnecessary third-party request from an
 * authentication surface. The explicit map keeps tree-shaking effective,
 * renders without a network, and makes an unknown name degrade locally to a
 * question mark instead of attempting a CDN lookup.
 */
const localIcons: Record<string, IconDefinition> = {
  book: faBook,
  building: faBuilding,
  'check-to-slot': faCheckToSlot,
  'circle-info': faCircleInfo,
  'clipboard-list': faClipboardList,
  database: faDatabase,
  envelope: faEnvelope,
  'file-lines': faFileLines,
  fingerprint: faFingerprint,
  gauge: faGauge,
  'heart-pulse': faHeartPulse,
  'id-badge': faIdBadge,
  key: faKey,
  laptop: faLaptop,
  link: faLink,
  'list-check': faListCheck,
  lock: faLock,
  'magnifying-glass': faMagnifyingGlass,
  scroll: faScroll,
  shield: faShield,
  'shield-halved': faShieldHalved,
  sitemap: faSitemap,
  sliders: faSliders,
  'square-check': faSquareCheck,
  ticket: faTicket,
  'triangle-exclamation': faTriangleExclamation,
  'user-shield': faUserShield,
  users: faUsers,
  xmark: faXmark,
}

export const createLocalIconAdapter = (): IconAdapter => ({
  render: ({ name, title, ariaHidden, className, style }) => {
    const definition = localIcons[name] ?? faCircleQuestion
    const [width, height, , , pathData] = definition.icon
    const paths = Array.isArray(pathData) ? pathData : [pathData]
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${width} ${height}`}
        width="1em"
        height="1em"
        fill="currentColor"
        focusable="false"
        aria-hidden={ariaHidden || undefined}
        role={ariaHidden ? undefined : 'img'}
        className={className}
        style={{ verticalAlign: '-0.125em', ...style }}
      >
        {!ariaHidden && title ? <title>{title}</title> : null}
        {paths.map((path) => (
          <path key={path} d={path} />
        ))}
      </svg>
    )
  },
})
